import { useMemo, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { ISignableEntity } from "../../../api/GenericTypes";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { Tile } from "../../../common/panels/Tile/Tile";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { NotSet } from "../../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { Button } from "../../../common/buttons/Button/Button";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { useDebouncedValue } from "../../../common/helperfunctions/useDebouncedValue";
import { Alert } from "../../../common/overlays/Alert/Alert";

export interface GenericEntitySignatureInformationHistoryButtonProps {
  btnClass?: string;
  hideLabel?: boolean;
  onClickCallback?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
}

export const GenericEntitySignatureInformationHistoryButton = <
  Entity extends GenericEntity & Partial<ISignableEntity>,
  ComponentProps extends GenericEntitySignatureInformationHistoryButtonProps
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const sortedSignatures = useMemo(() => {
    return entity?.signatures?.sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1)) || [];
  }, [entity?.signatures]);

  if (!entity || !entityConstants || !sortedSignatures.length) return null;
  return (
    <>
      <Button
        onClick={(e) => {
          setShowModal(true);
          componentProps?.onClickCallback?.(e);
        }}
        className={`btn btn-sm btn-ghost-secondary btn-block flex align-center gap-5`}
        title={`There are active signatures for this ${entityConstants.entitySingular}. Click to view.`}
      >
        <LucideIcon name="list-check" />
        {!componentProps?.hideLabel && "View signatures"}
      </Button>

      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <Modal.Body style={{ padding: "2px", overflow: "hidden", minHeight: "50vh", minWidth: "50vw" }}>
            <SignatureTile entity={entity} />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

const SignatureTile = <Entity extends GenericEntity & Partial<ISignableEntity>>({ entity }: { entity: Entity }) => {
  const [numberOfSignatures, setNumberOfSignatures] = useState<number>(50);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebouncedValue(searchValue, 100);
  const filteredSignatures = useMemo(() => {
    return (
      entity.signatures?.filter(
        (s) =>
          s.signedBy.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()) ||
          (s.comment?.toLowerCase().includes(debouncedSearchValue.toLowerCase()) ?? false)
      ) || []
    );
  }, [entity.signatures, debouncedSearchValue]);
  return (
    <Tile foldable={false} style={{ height: "100%" }}>
      <Tile.Head
        title={
          <div className="flex align-center gap-5">
            <LucideIcon name="signature" color="var(--primary)" />
            Signatures
          </div>
        }
      >
        <Tile.Head.Controls>
          <div className="flex align-center gap-5" style={{ width: "100%" }}>
            <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} />
          </div>
        </Tile.Head.Controls>
      </Tile.Head>
      <Tile.Body style={{ overflow: "auto", padding: "5px", height: "100%" }}>
        {!!filteredSignatures.length ? (
          <table className="table table-striped table-hover" style={{ width: "100%", marginBottom: "8px" }}>
            <thead>
              <tr>
                <th>Signed By</th>
                <th>Type</th>
                <th>Date</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {filteredSignatures.slice(0, numberOfSignatures).map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="flex align-center gap-5">
                      <LucideIcon name="signature" color="var(--success)" />
                      {s.signedBy.name}
                    </div>
                  </td>
                  <td>
                    <span className={`label label-soft-${s.signatureType === "Contributor" ? "primary" : "success"}`}>
                      {s.signatureType}
                    </span>
                  </td>
                  <td>
                    <div className="flex align-center gap-5">
                      <LucideIcon name="calendar" color="var(--gray-500)" />
                      <DateTimeRenderer date={s.timestamp} includeDate includeTime includeElapsed={false} />
                    </div>
                  </td>
                  <td>
                    <span title={s.comment ?? "No comment"}>{s.comment || NotSet}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Alert
            type="light"
            message={debouncedSearchValue ? `No signatures found for "${debouncedSearchValue}"` : "No signatures found"}
            fit
            centered
            style={{ margin: 0 }}
          />
        )}
        {!!filteredSignatures.length && numberOfSignatures < filteredSignatures.length && (
          <Button
            className="btn btn-soft-secondary btn-block flex align-center gap-5"
            style={{ justifyContent: "center" }}
            onClick={() => setNumberOfSignatures((prevState) => prevState + 50)}
          >
            <LucideIcon name="chevron-down" />
            View more
          </Button>
        )}
      </Tile.Body>
    </Tile>
  );
};
